import type { LngLatPoint } from './point-in-geojson-polygon';

export interface AddressSuggestion {
  id: string;
  label: string;
  point: LngLatPoint;
}

const MAPBOX_GEOCODING_URL = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

/** Below this, most providers return noisy single-letter matches rather than useful ones. */
const MIN_QUERY_LENGTH = 3;

/**
 * Forward-geocodes a free-text query (street address, city, postcode) to candidate points via
 * Mapbox, restricted to Finland. Client-side only, same privacy model as fetchCommuteIsochrone:
 * the query and result go straight to Mapbox (the same party already serving the map tiles) and
 * are never sent to our own backend or persisted.
 */
export async function searchAddressSuggestions(params: {
  query: string;
  accessToken: string;
  language?: string;
  signal?: AbortSignal;
}): Promise<AddressSuggestion[]> {
  const trimmed = params.query.trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return [];

  const search = new URLSearchParams({
    access_token: params.accessToken,
    country: 'fi',
    limit: '5',
    language: params.language ?? 'en',
    types: 'address,place,postcode,locality,neighborhood',
  });
  const response = await fetch(
    `${MAPBOX_GEOCODING_URL}/${encodeURIComponent(trimmed)}.json?${search.toString()}`,
    { signal: params.signal },
  );
  if (!response.ok) {
    throw new Error(`Geocoding request failed: ${response.status}`);
  }
  const body = (await response.json()) as {
    features?: Array<{ id?: string; place_name?: string; center?: [number, number] }>;
  };
  return (body.features ?? [])
    .filter((f): f is { id?: string; place_name?: string; center: [number, number] } =>
      Array.isArray(f.center) && f.center.length === 2,
    )
    .map((f, index) => ({
      id: f.id ?? `${f.place_name ?? 'result'}-${index}`,
      label: f.place_name ?? params.query,
      point: { lng: f.center[0], lat: f.center[1] },
    }));
}
